import React, { useState, useCallback, useMemo } from "react";
import { Select, Spin } from "antd";
import { useLazyQuery } from "@apollo/client";
import { SEARCH_USERS_QUERY } from "./SelectDoctor.gql";
import { SelectDoctorProps, Doctor } from "./SelectDoctor.types";

const { Option } = Select;

const SelectDoctor: React.FC<SelectDoctorProps> = ({
  value = [],
  onChange,
  placeholder = "Select Doctor",
  mode = "multiple",
  allowClear = true,
  style,
  className,
  disabled = false,
  doctorsOnly = true,
}) => {
  const [options, setOptions] = useState<Doctor[]>([]);

  const [searchUsers, { loading }] = useLazyQuery<{
    searchUsers: Doctor[];
  }>(SEARCH_USERS_QUERY, {
    fetchPolicy: "network-only",
    onCompleted: (data) => {
      const users = data?.searchUsers || [];
      const filtered = doctorsOnly
        ? users.filter((u) => u.isDoctor && !u.disabled)
        : users.filter((u) => !u.disabled);
      setOptions(filtered);
    },
  });

  const handleSearch = useCallback(
    (searchTerm: string) => {
      if (searchTerm.length >= 1) {
        searchUsers({ variables: { searchTerm } });
      }
    },
    [searchUsers]
  );

  const handleDropdownVisibleChange = useCallback(
    (open: boolean) => {
      if (open && options.length === 0) {
        searchUsers({ variables: { searchTerm: "" } });
      }
    },
    [searchUsers, options.length]
  );

  const handleChange = useCallback(
    (selectedValues: string[]) => {
      onChange?.(selectedValues);
    },
    [onChange]
  );

  const selectOptions = useMemo(
    () =>
      options.map((doctor) => (
        <Option key={doctor.id} value={doctor.id} label={doctor.name}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {doctor.color && (
              <span
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  backgroundColor: doctor.color,
                  display: "inline-block",
                }}
              />
            )}
            <span>{doctor.name}</span>
          </div>
        </Option>
      )),
    [options]
  );

  return (
    <Select
      mode={mode}
      value={value}
      onChange={handleChange}
      onSearch={handleSearch}
      placeholder={placeholder}
      allowClear={allowClear}
      style={style}
      className={className}
      disabled={disabled}
      showSearch
      filterOption={false}
      notFoundContent={loading ? <Spin size="small" /> : null}
      optionFilterProp="label"
      maxTagCount="responsive"
      onDropdownVisibleChange={handleDropdownVisibleChange}
    >
      {selectOptions}
    </Select>
  );
};

export default SelectDoctor;

